import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Container,
    Divider,
    Grid,
    LinearProgress,
    Stack,
    Typography
} from "@mui/material";
import {
    AccountBalanceWallet,
    ArrowForward,
    AutoStories,
    CalendarToday,
    EventAvailable,
    History
} from "@mui/icons-material";
import { useAuth } from "../hooks/useAuth";
import AdminLayout from "./AdminLayout";
import creditService from "../services/creditService";
import learningService from "../services/learningService";
import { getAllEvents } from "../services/eventService";
import StudentNoticeBoard from "../components/notices/StudentNoticeBoard";

const toDate = (value) => {
    if (!value) return null;
    if (value?.toDate) return value.toDate();
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export default function StudentDashboard() {
    const navigate = useNavigate();
    const { user, userProfile } = useAuth();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [creditHistory, setCreditHistory] = useState([]);
    const [eligibleEvents, setEligibleEvents] = useState([]);
    const [learningPaths, setLearningPaths] = useState([]);
    const [progresses, setProgresses] = useState([]);

    useEffect(() => {
        let active = true;
        async function fetchDashboardData() {
            if (!user?.uid) return;
            try {
                setLoading(true);
                setError("");
                const [credits, events, paths, progressData] = await Promise.all([
                    creditService.getUserCredits(user.uid, { limit: 10 }),
                    getAllEvents(),
                    learningService.getLearningPaths(true),
                    learningService.getStudentProgressSummary(user.uid)
                ]);
                if (!active) return;
                const profileCredits = Number(userProfile?.totalCredits || 0);
                const now = new Date();
                const filteredEvents = (events || [])
                    .filter((event) => (event.type || "internal") === "internal")
                    .filter((event) => !["Closed", "Completed"].includes(event.eventStatus || "Upcoming"))
                    .filter((event) => {
                        const cap = Number(event.credit_cap || 0);
                        const capacityAvailable = !event.maxCapacity || (event.registrations?.length || 0) < event.maxCapacity;
                        const registered = (event.registrations || []).some((reg) =>
                            reg.email === userProfile?.email || reg.studentId === (userProfile?.studentId || user.uid)
                        );
                        const dateValue = toDate(event.eventDate || event.date || event.lastDateToApply);
                        const deadlineValid = !dateValue || dateValue >= now;
                        return cap <= profileCredits && capacityAvailable && !registered && deadlineValid;
                    })
                    .sort((a, b) => {
                        const aDate = toDate(a.eventDate || a.date || a.lastDateToApply)?.getTime() || 0;
                        const bDate = toDate(b.eventDate || b.date || b.lastDateToApply)?.getTime() || 0;
                        return aDate - bDate;
                    })
                    .slice(0, 6);
                setCreditHistory(credits || []);
                setEligibleEvents(filteredEvents);
                setLearningPaths(paths || []);
                setProgresses(progressData || []);
            } catch (fetchError) {
                if (!active) return;
                setError(fetchError?.message || "Failed to load dashboard data.");
            } finally {
                if (active) setLoading(false);
            }
        }
        fetchDashboardData();
        const intervalId = setInterval(fetchDashboardData, 30000);
        return () => {
            active = false;
            clearInterval(intervalId);
        };
    }, [user?.uid, userProfile?.email, userProfile?.studentId, userProfile?.totalCredits]);

    const learningRows = useMemo(() => {
        const progressMap = new Map(progresses.map((p) => [p.pathId, p]));
        return learningPaths.map((path) => {
            const progress = progressMap.get(path.id);
            const levels = path.levels || [];
            const levelStatus = progress?.levelStatus || {};
            const cleared = Object.values(levelStatus).filter((status) => !!status.cleared).length;
            const total = Math.max(1, levels.length);
            const percent = Math.round((cleared / total) * 100);
            const nextLevel = levels.find((lvl) => !levelStatus[lvl.id]?.cleared);
            return {
                id: path.id,
                name: path.name,
                description: path.description,
                percent,
                completed: !!progress?.pathCompleted,
                clearedLevels: cleared,
                totalLevels: levels.length,
                nextStep: nextLevel?.name || "Review completed modules"
            };
        });
    }, [learningPaths, progresses]);

    const creditsBalance = Number(userProfile?.totalCredits || 0);

    if (loading) {
        return (
            <AdminLayout title="Student Dashboard">
                <Container maxWidth="xl" sx={{ py: 4 }}>
                    <Card variant="outlined">
                        <CardContent sx={{ py: 8, textAlign: "center" }}>
                            <CircularProgress />
                            <Typography color="text.secondary" sx={{ mt: 1.5 }}>
                                Loading your personalized dashboard...
                            </Typography>
                        </CardContent>
                    </Card>
                </Container>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout title="Student Dashboard">
            <Box sx={{ minHeight: "100vh", py: 3, bgcolor: "#f7f9fc" }}>
                <Container maxWidth="xl">
                    <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }} sx={{ mb: 2.5 }}>
                        <Box>
                            <Typography variant="h4" sx={{ fontWeight: 700, color: "#003366" }}>
                                Welcome, {userProfile?.firstName || "Student"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                View credits, upcoming events, and learning progress in one place.
                            </Typography>
                        </Box>
                        <Button
                            variant="outlined"
                            onClick={() => navigate("/student/learning/paths")}
                            endIcon={<ArrowForward />}
                        >
                            Browse Learning Paths
                        </Button>
                    </Stack>

                    {error && (
                        <Alert severity="error" sx={{ mb: 2.5 }}>
                            {error}
                        </Alert>
                    )}

                    <StudentNoticeBoard userId={user?.uid} />

                    <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
                        <Grid item xs={12} md={4}>
                            <Card variant="outlined" sx={{ height: "100%" }}>
                                <CardContent>
                                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                                        <AccountBalanceWallet sx={{ color: "#003366" }} />
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                            Current Credit Balance
                                        </Typography>
                                    </Stack>
                                    <Typography variant="h3" sx={{ fontWeight: 800, color: "#003366", lineHeight: 1.1 }}>
                                        {creditsBalance}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Live total based on your latest credit transactions
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <Card variant="outlined" sx={{ height: "100%" }}>
                                <CardContent>
                                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                                        <AutoStories sx={{ color: "#C1272D" }} />
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                            Personalized Learning Snapshot
                                        </Typography>
                                    </Stack>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                        {learningRows.filter((row) => row.completed).length} completed of {learningRows.length} active paths
                                    </Typography>
                                    <LinearProgress
                                        variant="determinate"
                                        value={learningRows.length ? Math.round((learningRows.filter((row) => row.completed).length / learningRows.length) * 100) : 0}
                                        sx={{ height: 8, borderRadius: 8, mb: 0.5 }}
                                    />
                                    <Typography variant="caption" color="text.secondary">
                                        Continue your current path to unlock more credits.
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    <Grid container spacing={2.5}>
                        <Grid item xs={12} lg={4}>
                            <Card variant="outlined" sx={{ height: "100%" }}>
                                <CardContent>
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                                        <History sx={{ color: "#003366" }} />
                                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                            Credit History
                                        </Typography>
                                    </Stack>
                                    {creditHistory.length === 0 ? (
                                        <Alert severity="info">No credit transactions yet.</Alert>
                                    ) : (
                                        <Stack spacing={1.25}>
                                            {creditHistory.map((item) => {
                                                const ts = toDate(item.createdAt);
                                                const isPositive = Number(item.amount || 0) >= 0;
                                                return (
                                                    <Box key={item.id} sx={{ p: 1.25, border: "1px solid #e4e7ec", borderRadius: 1.5 }}>
                                                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                                                            <Chip
                                                                size="small"
                                                                label={String(item.activityType || "activity").replace(/_/g, " ")}
                                                                variant="outlined"
                                                            />
                                                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: isPositive ? "#003366" : "#C1272D" }}>
                                                                {isPositive ? `+${item.amount}` : `${item.amount}`}
                                                            </Typography>
                                                        </Stack>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {item.description || "Credit activity"}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {ts ? ts.toLocaleString() : "Timestamp unavailable"}
                                                        </Typography>
                                                    </Box>
                                                );
                                            })}
                                            <Button size="small" onClick={() => navigate("/student/credits/history")}>
                                                View Full History
                                            </Button>
                                        </Stack>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} lg={4}>
                            <Card variant="outlined" sx={{ height: "100%" }}>
                                <CardContent>
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                                        <EventAvailable sx={{ color: "#C1272D" }} />
                                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                            Eligible Upcoming Events
                                        </Typography>
                                    </Stack>
                                    {eligibleEvents.length === 0 ? (
                                        <Alert severity="info">No eligible upcoming events right now.</Alert>
                                    ) : (
                                        <Stack spacing={1.25}>
                                            {eligibleEvents.map((event) => {
                                                const eventDate = toDate(event.eventDate || event.date || event.lastDateToApply);
                                                return (
                                                    <Box key={event.id} sx={{ p: 1.25, border: "1px solid #e4e7ec", borderRadius: 1.5 }}>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#003366", mb: 0.5 }}>
                                                            {event.title}
                                                        </Typography>
                                                        <Stack direction="row" spacing={1} sx={{ mb: 0.5, flexWrap: "wrap" }}>
                                                            <Chip size="small" icon={<CalendarToday />} label={eventDate ? eventDate.toLocaleDateString() : "Date TBA"} />
                                                            <Chip size="small" label={`Req: ${Number(event.credit_cap || 0)} credits`} variant="outlined" />
                                                        </Stack>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {event.venue || "Venue TBA"} {event.eventTime ? `• ${event.eventTime}` : ""}
                                                        </Typography>
                                                    </Box>
                                                );
                                            })}
                                            <Button size="small" onClick={() => navigate("/student/events")}>
                                                Browse Events
                                            </Button>
                                        </Stack>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} lg={4}>
                            <Card variant="outlined" sx={{ height: "100%" }}>
                                <CardContent>
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                                        <AutoStories sx={{ color: "#003366" }} />
                                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                            Learning Paths
                                        </Typography>
                                    </Stack>
                                    {learningRows.length === 0 ? (
                                        <Alert severity="info">No learning paths assigned yet.</Alert>
                                    ) : (
                                        <Stack spacing={1.25}>
                                            {learningRows.slice(0, 4).map((row) => (
                                                <Box key={row.id} sx={{ p: 1.25, border: "1px solid #e4e7ec", borderRadius: 1.5 }}>
                                                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#003366" }}>
                                                            {row.name}
                                                        </Typography>
                                                        <Chip size="small" label={row.completed ? "Completed" : "In Progress"} color={row.completed ? "success" : "default"} />
                                                    </Stack>
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
                                                        {row.clearedLevels}/{row.totalLevels} modules completed
                                                    </Typography>
                                                    <LinearProgress variant="determinate" value={row.percent} sx={{ height: 6, borderRadius: 6, mb: 0.75 }} />
                                                    <Typography variant="caption" color="text.secondary">
                                                        Next step: {row.nextStep}
                                                    </Typography>
                                                </Box>
                                            ))}
                                            <Divider />
                                            <Button size="small" onClick={() => navigate("/student/learning/progress")}>
                                                View Detailed Progress
                                            </Button>
                                        </Stack>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Container>
            </Box>
        </AdminLayout>
    );
}
